import React from 'react'
import moment from 'moment'
import { Set } from 'immutable'

// @src imports
import {
  useAccount,
  useEffect,
  useLocalStorage,
  useMutations,
  usePersistedUserData,
  useQueries,
  useReducer,
} from 'src/hooks'

import useActions from 'src/hooks/useActions'
import { Category as ItemCategory, Item } from 'src/campaign_store/types'
import { ACCOUNT_TOOLS_ID } from 'src/app/constants'

import {
  AffiliatePaymentInfo,
  ItemsPaymentInfo,
  PurchaseTypes,
} from 'src/payments/containers/Payment'

import { JoinModal } from 'src/user/components'

import {
  Confirm,
  DefaultError,
  NavLink,
  PersistState,
  Transition,
} from 'src/chrome'

import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'

import { getItemCategory } from 'src/graphql'

import { PLAN_ORDER } from 'src/marketing/plans/constants'

import { PlanDrawer } from 'src/marketing/plans/components'
import { PaymentModal } from 'src/payments/components'
import { noop } from 'src/helpers/appHelpers'

// relative imports
import { Plan } from '../types'

import {
  AccountStorage,
  AffiliateForm,
  AutomatedGSendsAndCampaignGrid,
  ContentSplitter,
  PlanContentsGrid,
} from '../components'

import { AffiliateFormInfo } from '../components/AffiliateForm/AffiliateForm'
import { useState } from '../../../hooks'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import WaysToSend from '../components/WaysToSend/WaysToSend'
import CampaignStoreAddOn from '../components/CampaignStoreAddOn/CampaignStoreAddOn'
import Plans from 'src/plans/containers/Plans'
import { Spacer } from '@sendoutcards/quantum-design-ui'
import { enabledPlans as plans } from 'src/marketing/plans/plansInfo'

const styles = {
  pricingContainer: {
    width: '100%',
    fontFamily: "'Open Sans', sans-serif",
  },
}

type PlansProps = {
  route?: PricingRoute
}

type State = {
  accountTools?: ItemCategory | null
  activeItem?: Item | null
  upgradeConfirm: boolean
  isCancelConfirmationVisible: boolean
  isKeepSubscriptionVisible: boolean
  isCanceling: boolean
  downgrade?: string
  selectedPlan?: Plan
  selectedItem: Item | null
  purchasing?: boolean
  isPaymentModalOpen: boolean
  paymentInfo?: ItemsPaymentInfo | AffiliatePaymentInfo
  isAffiliate: boolean
  isJoinModalOpen: boolean
  next: string | null
}

const initialState: State = {
  upgradeConfirm: false,
  isCancelConfirmationVisible: false,
  isKeepSubscriptionVisible: false,
  isCanceling: false,
  selectedItem: null,
  purchasing: false,
  isPaymentModalOpen: false,
  isAffiliate: false,
  isJoinModalOpen: false,
  next: null,
}

type Action =
  | { type: 'set'; payload: { [P in keyof State]?: State[P] } }
  | { type: 'reset' }

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'set':
      return {
        ...state,
        ...action.payload,
      }
    case 'reset':
    default:
      return {
        ...state.accountTools,
        ...initialState,
      }
  }
}

const Pricing: React.FC<PlansProps> = props => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const {
    accountTools,
    activeItem,
    isCancelConfirmationVisible,
    isKeepSubscriptionVisible,
    downgrade,
    selectedPlan,
    isPaymentModalOpen,
    paymentInfo,
    isAffiliate,
    isJoinModalOpen,
    next,
  } = state

  const { route } = props

  const [isPlanDrawerOpen, setIsPlanDrawerOpen] = useState(false)
  const [planTitle, setPlanTitle] = useState<string>('Enhanced')
  const [addOnTitles, setAddOnTitles] = useState<Set<string>>(Set())

  const actions = useActions()
  const storage = useLocalStorage()

  const { controlData } = usePersistedUserData()
  const account = useAccount()

  const mutations = useMutations()

  const [itemCategory] = useQueries(getItemCategory({ id: ACCOUNT_TOOLS_ID }))

  useEffect(() => {
    loadAccountTools()
    if (route?.affiliate) {
      dispatch({
        type: 'set',
        payload: {
          isAffiliate: true,
        },
      })
    }
    if (route?.plan !== undefined) {
      showPlanUpgrade(route.plan)
    }

    return () => {
      storage?.removeItem('join-page')
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  const handleAffiliate = () => {
    const hasAccount = !!account.username

    dispatch({
      type: 'set',
      payload: {
        isAffiliate: hasAccount,
        isJoinModalOpen: !hasAccount,
        next: !hasAccount ? 'handleAffiliate' : null,
      },
    })
  }

  const onCloseModal = () => {
    dispatch({ type: 'reset' })
  }

  const onFinishJoin = () => {
    if (next) {
      if (
        next === 'showPlanUpgrade' &&
        selectedPlan &&
        selectedPlan.title === 'Basic'
      ) {
        window.location.reload(false)
      } else if (next === 'handleAffiliate') {
        handleAffiliate()
      }
    }

    dispatch({
      type: 'set',
      payload: {
        next: null,
      },
    })
  }

  const closeCancel = () => {
    dispatch({
      type: 'set',
      payload: {
        isCancelConfirmationVisible: false,
      },
    })
  }

  const confirmCancel = async () => {
    dispatch({
      type: 'set',
      payload: {
        isCancelConfirmationVisible: false,
        isKeepSubscriptionVisible: false,
        isCanceling: true,
      },
    })
    try {
      const {
        updateAccount: { account },
      } = await mutations.updateAccount({
        account: {
          plan: selectedPlan?.id ?? '1',
        },
      })
      actions.updatedAccount?.(account)
      dispatch({ type: 'reset' })
    } catch (err) {
      handlePurchaseError()
    }
  }

  const showPlanUpgrade = (planTitle: string, isKeepButton?: boolean) => {
    const plan = plans.find(plan => plan.title === planTitle) ?? selectedPlan

    if (!plan) {
      return
    }

    if (isKeepButton) {
      dispatch({
        type: 'set',
        payload: {
          isKeepSubscriptionVisible: true,
          selectedPlan: plan,
        },
      })
      return
    }

    const canDowngrade =
      plan &&
      PLAN_ORDER.indexOf(plan.title) < PLAN_ORDER.indexOf(account.plan.title)

    if (canDowngrade) {
      dispatch({
        type: 'set',
        payload: {
          downgrade: plan.title,
          isCancelConfirmationVisible: true,
          selectedPlan: plan,
        },
      })
    } else {
      setPlanTitle(plan.title)
      setAddOnTitles(Set())
      setIsPlanDrawerOpen(true)
    }
  }

  const loadAccountTools = () => {
    dispatch({
      type: 'set',
      payload: {
        accountTools: {
          ...itemCategory,
          children: itemCategory.children.map(child => ({
            ...child,
            children: [],
          })),
        },
      },
    })
  }

  const purchase = (item: Item) => {
    dispatch({
      type: 'set',
      payload: {
        activeItem: null,
        isPaymentModalOpen: true,
        paymentInfo: {
          items: [item],
          purchaseType: PurchaseTypes.ITEMS,
        },
      },
    })
  }

  const purchaseAffiliate = (data: AffiliateFormInfo) => {
    dispatch({
      type: 'set',
      payload: {
        isAffiliate: false,
        isPaymentModalOpen: true,
        paymentInfo: {
          data,
          purchaseType: PurchaseTypes.DISTRIBUTOR,
        },
      },
    })
  }

  const handlePurchaseSuccess = () => {
    if (next) {
      window.location.reload(false)
    }
  }

  const handlePurchaseError = () => {
    dispatch({ type: 'reset' })
  }

  const showProductPreview = (item: Item) => {
    dispatch({
      type: 'set',
      payload: {
        activeItem: item,
      },
    })
  }

  const closeProductPreview = () => {
    dispatch({
      type: 'set',
      payload: {
        activeItem: null,
      },
    })
  }

  useEffect(() => {
    if (isPlanDrawerOpen) {
      document.body.style.overflow = 'hidden'
    }

    if (!isPlanDrawerOpen) {
      document.body.style.overflow = 'unset'
    }
  }, [isPlanDrawerOpen])

  const subscription = account.stripeSubscription

  return (
    <div>
      <PaymentModal
        isOpen={controlData.isLoggedIn && isPaymentModalOpen}
        paymentInfo={paymentInfo}
        onClose={() => {
          dispatch({
            type: 'set',
            payload: {
              isPaymentModalOpen: false,
              paymentInfo: undefined,
            },
          })
        }}
        onSuccess={
          paymentInfo?.purchaseType === PurchaseTypes.DISTRIBUTOR
            ? handlePurchaseSuccess
            : noop
        }
        onError={
          paymentInfo?.purchaseType === PurchaseTypes.DISTRIBUTOR
            ? handlePurchaseError
            : noop
        }
      />
      <div css={styles.pricingContainer}>
        <PersistState
          name="join-page"
          data={state}
          onMount={data => {
            dispatch({
              type: 'set',
              payload: {
                ...data,
                isAffiliate: route?.affiliate,
                purchasing: false,
                upgradeConfirm: false,
                isJoinModalOpen: false,
              },
            })
          }}
        />
        <Plans onSelect={showPlanUpgrade} />
        <WaysToSend />
        <PlanContentsGrid />
        <AutomatedGSendsAndCampaignGrid />
        <AccountStorage />
        <CampaignStoreAddOn
          accountTools={accountTools}
          activeItem={activeItem}
          purchase={purchase}
          showProductPreview={showProductPreview}
          closeProductPreview={closeProductPreview}
        />
        {account && (
          <>
            <NavLink onClick={() => actions.openCampaignStore()}>
              <ContentSplitter
                title={'View Campaign Store'}
                description={
                  'We have campaigns for just about every niche you can think of.'
                }
                icon={{
                  icon: 'CART',
                  size: 30,
                  viewBox: '0 0 35 32',
                  color: '#fff',
                }}
              />
            </NavLink>
            <Spacer space="x8" />
          </>
        )}
        {isCancelConfirmationVisible && (
          <Confirm
            title={`Downgrade ${account.plan.title} Subscription`}
            message={`
              Your subscription will be downgraded to ${downgrade} at the end of the current billing period:
              ${
                subscription &&
                moment(subscription.currentPeriodEnd).format(
                  'ddd MMM DD, YYYY HH:mm a',
                )
              }
            `}
            confirmTitle={'Continue'}
            onConfirm={confirmCancel}
            onDecline={closeCancel}
            isModalEnabled={true}
          />
        )}
        {isKeepSubscriptionVisible && (
          <Confirm
            title={`Keep current subscription`}
            message={`
              Do not change my subscription. I want to keep my current subscription
            `}
            confirmTitle={'Confirm'}
            onConfirm={confirmCancel}
            onDecline={closeCancel}
            isModalEnabled={true}
          />
        )}
        {isAffiliate && (
          <AffiliateForm close={onCloseModal} purchase={purchaseAffiliate} />
        )}
        {isJoinModalOpen && (
          <JoinModal
            initialAction={'join'}
            onClose={onCloseModal}
            onAfterSave={onFinishJoin}
          />
        )}
        {isPlanDrawerOpen && (
          <PlanDrawer
            onClose={() => setIsPlanDrawerOpen(false)}
            planTitle={planTitle}
            addOnTitles={addOnTitles}
          />
        )}
      </div>
    </div>
  )
}

export default suspenseBoundary({
  component: Pricing,
  unresolved: <Transition message={'Loading...'} />,
  failure: DefaultError,
})
